package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.*
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * Stage 5：await 目标校验 pass。
 *
 * 在 NativeClassResolverPass 之后运行。此时 AwaitExpr.netRequestCall 中的 MemberCall
 * 已被 NativeClassResolverPass 递归 lower 为 NetRequestCall。本 pass 负责：
 * 1. 校验所有 AwaitExpr.netRequestCall 确实已是 NetRequestCall（lower 完成的 sanity check）
 * 2. 检查 NetRequestCall.callback 必须为 null（E603：await 不允许携带 callback）
 */
class AwaitTargetResolverPass(
    private val diag: DiagnosticCollector,
) {

    /** 入口：遍历 screen 中所有 AwaitExpr 做校验。 */
    fun expand(screen: ScreenAst): ScreenAst {
        walkElement(screen.root)
        screen.funs.forEach { walkFunDecl(it) }
        screen.onKeyLambda?.let { walkLambda(it) }
        screen.onMouseDownLambda?.let { walkLambda(it) }
        screen.onMouseUpLambda?.let { walkLambda(it) }
        screen.onMouseMoveLambda?.let { walkLambda(it) }
        screen.openBody?.let { walkStmtList(it) }
        screen.closeBody?.let { walkStmtList(it) }
        screen.showBody?.let { walkStmtList(it) }
        screen.hideBody?.let { walkStmtList(it) }
        screen.papiBlocks.forEach { walkPapiBlock(it) }
        screen.subscribeBlocks.forEach { walkSubscribeBlock(it) }
        // 不修改 AST，仅做诊断
        return screen
    }

    // ===== 结构遍历 =====

    private fun walkFunDecl(decl: FunDecl) {
        walkStmtList(decl.body)
        decl.defaults.forEach { it?.let { walkExpr(it) } }
    }

    private fun walkLambda(lambda: ExprAst.LambdaExpr) {
        walkStmtList(lambda.body)
    }

    private fun walkElement(element: ElementAst) {
        element.props.forEach { walkExpr(it.value) }
        element.modifiers.forEach { modifier ->
            modifier.fields.forEach { walkExpr(it.value) }
        }
        element.children.forEach { walkElement(it) }
        element.sourceExpr?.let { walkExpr(it) }
    }

    private fun walkPapiBlock(block: PapiBlockAst) {
        block.placeholders.forEach { ph ->
            ph.bindExpr?.let { walkExpr(it) }
            ph.pipe?.let { walkLambda(it) }
            ph.pipeExpr?.let { walkExpr(it) }
            ph.onChangeExpr?.let { walkExpr(it) }
        }
    }

    private fun walkSubscribeBlock(block: SubscribeBlockAst) {
        block.items.forEach { walkExpr(it.handler) }
    }

    // ===== 语句遍历 =====

    private fun walkStmtList(stmts: List<StmtAst>) {
        stmts.forEach { walkStmt(it) }
    }

    private fun walkStmt(s: StmtAst) {
        when (s) {
            is StmtAst.ExprStmt -> walkExpr(s.call)
            is StmtAst.AssignStmt -> walkExpr(s.rhs)
            is StmtAst.VarDeclStmt -> walkExpr(s.initializer)
            is StmtAst.IfStmt -> {
                walkExpr(s.cond)
                walkStmtList(s.thenBody)
                walkStmtList(s.elseBody)
            }
            is StmtAst.WhileStmt -> {
                walkExpr(s.cond)
                walkStmtList(s.body)
            }
            is StmtAst.ForInStmt -> {
                walkExpr(s.iterable)
                walkStmtList(s.body)
            }
            is StmtAst.WhenStmt -> walkExpr(s.whenExpr)
            is StmtAst.ReturnStmt -> s.value?.let { walkExpr(it) }
            is StmtAst.TryStmt -> {
                walkStmtList(s.tryBody)
                walkStmtList(s.catchBody)
                walkStmtList(s.finallyBody)
            }
            // batch 语句：递归遍历 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> walkStmtList(s.body)
            is StmtAst.FunDeclStmt -> walkFunDecl(s.decl)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> {}
        }
    }

    // ===== 表达式遍历 =====

    private fun walkExpr(expr: ExprAst) {
        when (expr) {
            is ExprAst.AwaitExpr -> {
                val nr = expr.netRequestCall
                if (nr != null) {
                    if (nr is ExprAst.NetRequestCall) {
                        // lower 完成，检查 callback 必须为 null
                        if (nr.callback != null) {
                            diag.error(DslError.E603_AWAIT_HAS_CALLBACK, expr.position)
                        }
                    } else {
                        // P1 防御：NativeClassResolverPass 应已 lower MemberCall 为 NetRequestCall；
                        // 若到达此处仍是 MemberCall（或其它非 NetRequestCall 形态），属于 pipeline 失序，
                        // 上报 E603 让用户至少看到错误，避免 codegen 阶段 ClassCastException 静默失败。
                        diag.error(DslError.E603_AWAIT_HAS_CALLBACK, expr.position)
                    }
                    walkExpr(nr)
                }
                expr.suspendCall?.let { walkExpr(it) }
                expr.tailExpr?.let { walkExpr(it) }
            }
            is ExprAst.BinaryOp -> { walkExpr(expr.left); walkExpr(expr.right) }
            is ExprAst.UnaryOp -> walkExpr(expr.operand)
            is ExprAst.MemberAccess -> walkExpr(expr.target)
            is ExprAst.SafeMemberAccess -> walkExpr(expr.target)
            // M10 Stage 6：`target!!` 仅穿透 target
            is ExprAst.NonNullAssert -> walkExpr(expr.target)
            is ExprAst.MemberCall -> {
                walkExpr(expr.target)
                expr.args.forEach { walkExpr(it) }
                expr.namedArgs.forEach { walkExpr(it.value) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExpr(expr.target)
                expr.args.forEach { walkExpr(it) }
            }
            is ExprAst.Elvis -> { walkExpr(expr.left); walkExpr(expr.right) }
            is ExprAst.Ternary -> {
                walkExpr(expr.cond); walkExpr(expr.thenExpr); walkExpr(expr.elseExpr)
            }
            is ExprAst.IfExpr -> {
                walkExpr(expr.cond)
                walkStmtList(expr.thenBody)
                walkStmtList(expr.elseBody)
            }
            is ExprAst.Interp -> expr.parts.forEach { part ->
                if (part is ExprAst.Interp.InterpPart.ExprPart) walkExpr(part.expr)
            }
            is ExprAst.ListLiteralExpr -> expr.elements.forEach { walkExpr(it) }
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { walkExpr(it.value) }
            is ExprAst.RangeExpr -> { walkExpr(expr.start); walkExpr(expr.endValue) }
            is ExprAst.IndexExpr -> { walkExpr(expr.target); walkExpr(expr.index) }
            is ExprAst.LambdaExpr -> walkStmtList(expr.body)
            is ExprAst.WhenExpr -> {
                walkExpr(expr.subject)
                expr.branches.forEach { br ->
                    br.values.forEach { walkExpr(it) }
                    walkStmtList(br.body)
                }
                expr.elseBody?.let { walkStmtList(it) }
            }
            is ExprAst.CallExpr -> {
                walkExpr(expr.callee)
                expr.args.forEach { walkExpr(it) }
                expr.namedArgs.forEach { walkExpr(it.value) }
            }
            is ExprAst.BuiltinCallExpr -> {
                expr.args.forEach { walkExpr(it) }
                expr.namedArgs.forEach { walkExpr(it.value) }
            }
            is ExprAst.NetRequestCall -> {
                expr.payload.forEach { walkExpr(it.value) }
                expr.callback?.let { walkExpr(it) }
                expr.timeoutMs?.let { walkExpr(it) }
            }
            is ExprAst.NetSubscribeCall -> walkExpr(expr.handler)
            is ExprAst.IsExpr -> walkExpr(expr.target)
            // 叶子节点
            is ExprAst.Literal, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.ThemeRef, is ExprAst.PropsRef,
            is ExprAst.NullLit, is ExprAst.FunRefExpr -> {}
        }
    }
}
